import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@ds/components/core/Button';
import { Card, CardBody, CardHeader } from '@ds/components/core/Card';
import { AuthenticatedShell, Page } from '@/components/AppShell';
import { useToast } from '@/components/ToastProvider';
import { useLogout, useSession } from '@/features/auth/api';
import { ChangePasswordForm } from '@/features/users/components/ChangePasswordForm';
import { DeleteAccountDialog } from '@/features/users/components/DeleteAccountDialog';
import { ResetAccountDialog } from '@/features/users/components/ResetAccountDialog';
import { ProfileForm } from '@/features/users/components/ProfileForm';
import { TransactionsViewModeForm } from '@/features/users/components/TransactionsViewModeForm';
import { EXPORT_DATA_URL } from '@/features/users/api';
import { TelegramLinkCard } from '@/features/telegram/components/TelegramLinkCard';
import { FamilyCard } from '@/features/family/components/FamilyCard';

export const Route = createFileRoute('/_authenticated/perfil')({ component: ProfilePage });

function ProfilePage() {
  const { data: user } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const toast = useToast();

  if (!user) return null;

  return (
    <AuthenticatedShell
      title="Perfil"
      right={
        <Button
          variant="secondary"
          data-testid="profile-logout"
          loading={logout.isPending}
          onClick={() => logout.mutate(undefined, { onSettled: () => void navigate({ to: '/entrar' }) })}
        >
          Sair
        </Button>
      }
    >
      <Page>
        <div style={{ display: 'grid', gap: 'var(--gutter-section)' }}>
          <Card>
            <CardHeader title="Dados pessoais" />
            <CardBody>
              <ProfileForm user={user} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Segurança" description="Altere sua senha periodicamente." />
            <CardBody>
              <ChangePasswordForm />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Telegram" description="Vincule sua conta pra registrar transações mandando uma mensagem de texto pro bot." />
            <CardBody>
              <TelegramLinkCard />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Família" description="Convide outras pessoas pra ver dados agregados ou isolados na dashboard e nas transações." />
            <CardBody>
              <FamilyCard />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Preferências" description="Como você quer ver a tela de Transações." />
            <CardBody>
              <TransactionsViewModeForm user={user} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Privacidade e dados" />
            <CardBody>
              <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <a href={EXPORT_DATA_URL} className="ca-btn ca-btn--secondary" data-testid="export-data">
                  Baixar meus dados
                </a>
                <DeleteAccountDialog onDeleted={() => void navigate({ to: '/entrar' })} />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Zona de risco" description="Ações destrutivas — não podem ser desfeitas." />
            <CardBody>
              <ResetAccountDialog
                onReset={(result) => {
                  toast({
                    tone: 'positive',
                    title: 'Conta resetada',
                    description: `${result.transactionsDeleted} transaç${result.transactionsDeleted === 1 ? 'ão' : 'ões'}, ${result.accountsDeleted} conta${result.accountsDeleted === 1 ? '' : 's'} e ${result.cardsDeleted} cartã${result.cardsDeleted === 1 ? 'ão' : 'ões'} apagados.`,
                  });
                  void navigate({ to: '/' });
                }}
              />
            </CardBody>
          </Card>
        </div>
      </Page>
    </AuthenticatedShell>
  );
}
